import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, animate, useInView } from "framer-motion";
import { ArrowRight, Flame, BookOpen, Users } from "lucide-react";
import { api, supporterHeaders } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";
import PaperCard from "@/components/PaperCard";

const HERO_IMG =
  "https://images.unsplash.com/photo-1780956247819-8da5554b0074?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxxdWFudHVtJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3ODkzMTc0MDJ8MA&ixlib=rb-4.1.0&q=85";

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function VaultCounter() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, 169200, {
      duration: 2.4,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setN(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView]);
  return <span ref={ref} data-testid="vault-counter">{n.toLocaleString()}+</span>;
}

const CHAPTERS = [
  {
    num: "01",
    title: "The stakes are now",
    body: "This is about the technology that is crucial today for creating the world tomorrow. Quantum meshes, peer-to-peer energy, edge intelligence, open genomics — the breakthroughs being written down this decade decide what the next one looks like.",
  },
  {
    num: "02",
    title: "Breakthroughs compound",
    body: "No single invention builds the future. Combining technical breakthroughs directly indulges the new machinery that creates tomorrow for all — energy markets feeding edge compute, open genomics riding open hardware. We publish research at those intersections.",
  },
  {
    num: "03",
    title: "Readers are the fund",
    body: "No gatekeepers, no grant committees. Every paper is free to read, and every donation flows straight into the next study. Become a member, donate what you can, and help this site — and the technology it documents — grow.",
  },
];

export default function Home() {
  const { stats } = useSite();
  const [featured, setFeatured] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 130]);

  useEffect(() => {
    api.get("/papers", supporterHeaders()).then((r) => {
      const feat = r.data.filter((p) => p.featured);
      setFeatured(feat.length ? feat : r.data.slice(0, 3));
    }).catch(() => {});
  }, []);

  return (
    <main>
      {/* ---------- HERO ---------- */}
      <section ref={heroRef} className="relative hero-glow noise-overlay overflow-hidden">
        <div className="absolute inset-0 grid-lines" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center pt-16 pb-24">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              data-testid="hero-manifesto-badge"
              className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-sky-400 border border-sky-400/30 bg-sky-400/5 rounded-full px-4 py-1.5 mb-8"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-sky-400 animate-pulse" />
              Manifesto — Vol. 01 · Open Access
            </motion.div>

            <h1 data-testid="hero-title" className="font-display font-extrabold tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl">
              <MaskedLine delay={0.15}>The technology</MaskedLine>
              <MaskedLine delay={0.3}>of today builds</MaskedLine>
              <MaskedLine delay={0.45}>
                <span className="text-sky-400 text-glow-cyan">the world of tomorrow.</span>
              </MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.75 }}
              className="mt-7 text-base sm:text-lg text-slate-400 leading-relaxed max-w-xl"
            >
              SoverTech publishes independent white papers on the breakthroughs that matter
              right now — and shows how combining them creates the machinery of the future,
              for everyone. Free to read. Funded directly by readers like you.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/library"
                data-testid="hero-cta-explore"
                className="group bg-sky-400 text-[#07090E] font-semibold text-sm rounded-full px-7 py-3.5 hover:bg-sky-300 transition-colors flex items-center gap-2"
              >
                Explore the Library
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/donate"
                data-testid="hero-cta-donate"
                className="border border-amber-400/50 text-amber-400 font-semibold text-sm rounded-full px-7 py-3.5 hover:bg-amber-400/10 transition-colors flex items-center gap-2"
              >
                <Flame className="w-4 h-4" /> Become a Supporter
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.1 }}
              className="mt-12 grid grid-cols-3 gap-6 max-w-md"
            >
              {[
                { icon: BookOpen, label: "Papers live", value: stats?.papers ?? "—", testid: "hero-stat-papers" },
                { icon: Flame, label: "Raised so far", value: stats ? `$${(stats.raised || 0).toLocaleString()}` : "—", testid: "hero-stat-donations" },
                { icon: Users, label: "Supporters", value: stats?.supporters ?? "—", testid: "hero-stat-supporters" },
              ].map((s) => (
                <div key={s.label} data-testid={s.testid} className="border-l border-[#1E2638] pl-4">
                  <s.icon className="w-4 h-4 text-sky-400 mb-2" />
                  <p className="font-display font-bold text-xl">{s.value}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">{s.label}</p>
                </div>
              ))}
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.5 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative rounded-2xl overflow-hidden border border-[#1E2638]" style={{ clipPath: "polygon(0 0, 100% 4%, 100% 100%, 0 96%)" }}>
              <motion.img
                src={HERO_IMG}
                alt="Quantum technology research apparatus"
                style={{ y: imgY, scale: 1.15 }}
                className="w-full h-[420px] lg:h-[520px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#07090E]/90 via-transparent to-[#07090E]/30" />
              <div className="absolute bottom-5 left-5 right-5 font-mono text-[10px] uppercase tracking-[0.2em] text-sky-300/80 flex justify-between">
                <span>FIG. 01 — FRONTIER LAB</span>
                <span className="text-slate-500">SOVERTECH ARCHIVE</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Marquee />

      {/* ---------- MANIFESTO CHAPTERS ---------- */}
      <section id="manifesto" className="relative py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-sky-400 mb-4">The Manifesto</p>
            <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl max-w-2xl">
              Three commitments. One research fund. Zero gatekeepers.
            </h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-14">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.num} delay={i * 0.15}>
                <div data-testid={`manifesto-chapter-${c.num}`} className="group border-t-2 border-[#1E2638] hover:border-sky-400/60 pt-8 transition-colors duration-500">
                  <p className="font-mono text-5xl font-bold text-[#1E2638] group-hover:text-sky-400/40 transition-colors duration-500 mb-6">
                    {c.num}
                  </p>
                  <h3 className="font-display font-semibold text-xl mb-4">{c.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- FOUNDER'S STATEMENT ---------- */}
      <section className="py-24 sm:py-28 border-t border-[#1E2638] relative noise-overlay" data-testid="founder-statement">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber-400 mb-8">From the Founder — The Preamble</p>
            <p className="font-display font-bold text-2xl sm:text-3xl leading-snug text-slate-100 mb-8">
              "True infrastructure is neither rented nor debated — it is engineered into the fundamental laws of matter."
            </p>
            <div className="space-y-5 text-slate-400 leading-relaxed text-base sm:text-lg">
              <p>
                For too long, technological development has been weighed down by layers of
                abstraction, inefficient code, and external points of failure. We have
                established a different path.
              </p>
              <p>
                Within our secure repository lies a comprehensive vault of advanced structural
                systems, multi-layered carbon-hydrogen lattices, and self-contained mechanical
                frameworks designed to operate independently of legacy electrical grids and
                centralized control. We do not patch old systems; we build new realities from
                the atomic layer up.
              </p>
              <p className="text-slate-200 font-medium">
                What you see on the surface is only a fraction of the architecture. The
                framework is complete, the blueprints are locked, and the execution begins now.
              </p>
            </div>
            <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-8" data-testid="vault-stats">
              <div className="border-l-2 border-amber-400/50 pl-5">
                <p className="font-display font-extrabold text-3xl sm:text-4xl text-amber-400"><VaultCounter /></p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-2">
                  Documented invention concepts secured
                </p>
              </div>
              <div className="border-l-2 border-[#1E2638] pl-5">
                <p className="font-display font-extrabold text-3xl sm:text-4xl text-slate-100">$1.75M</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-2">
                  Fabrication target, in BTC
                </p>
              </div>
              <div className="border-l-2 border-[#1E2638] pl-5">
                <p className="font-display font-extrabold text-3xl sm:text-4xl text-sky-400">01</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-2">
                  Atomic printer unlocks the vault
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- FEATURED PAPERS ---------- */}
      <section className="py-24 bg-[#0F131C] border-y border-[#1E2638]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-sky-400 mb-4">From the archive</p>
              <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl">
                Featured white papers
              </h2>
            </div>
            <Link
              to="/library"
              data-testid="featured-view-all"
              className="group font-mono text-xs uppercase tracking-[0.2em] text-slate-400 hover:text-sky-400 transition-colors flex items-center gap-2"
            >
              View full library
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {featured.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.12}>
                <PaperCard paper={p} index={i} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- SUPPORT CTA ---------- */}
      <section className="relative py-28 noise-overlay overflow-hidden">
        <div className="absolute inset-0 hero-glow" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber-400 mb-6">Fuel the next paper</p>
            <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight">
              The machinery of tomorrow is<br className="hidden sm:block" />
              <span className="text-amber-400"> funded by readers today.</span>
            </h2>
            <p className="mt-6 text-slate-400 leading-relaxed max-w-2xl mx-auto">
              Become a member of the SoverTech fund. Your donation keeps every paper free,
              finances the next study, and unlocks our supporters-only deep dives.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link
                to="/donate"
                data-testid="support-cta-donate"
                className="bg-amber-400 text-[#07090E] font-semibold text-sm rounded-full px-8 py-4 hover:bg-amber-300 transition-colors"
              >
                Donate &amp; Unlock Supporter Access
              </Link>
              <Link
                to="/library"
                data-testid="support-cta-read"
                className="border border-[#1E2638] text-slate-300 font-semibold text-sm rounded-full px-8 py-4 hover:border-sky-400/50 hover:text-sky-300 transition-colors"
              >
                Keep Reading First
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
